import { useState, useMemo } from 'react';
import { ROADMAP, STATUS_META, TAG_META, EFFORT_META, ROLE_COLORS, INVESTOR_METRICS } from '../../data/roadmap';
import { PageHeader, StatCard } from '../../components/common/UI';

/**
 * Admin-only roadmap viewer. Mirrors ROADMAP.md at the repo root with the
 * same items but rendered as a filterable, scannable, searchable dashboard.
 *
 * Layout draws on Vowsera's tracker pattern (vowsera-client/.../FeatureRoadmap.jsx):
 *   - Overall stacked progress bar at the top
 *   - Phase pills + status filter pills + search box
 *   - Per-phase header card with its own progress bar
 *   - Items render as a click-to-expand accordion with full detail panel
 *   - Footer card with investor / platform metric snapshot
 *
 * Visibility: this component is rendered inside AdminDashboard, which is
 * already gated by `requireAdmin` in App.jsx. No extra access check
 * needed here — non-super_admins can never reach this route.
 */
export default function FeatureRoadmap() {
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null); // `${phaseIdx}:${itemIdx}` of the open accordion item

  // Aggregate counts across all phases for the stat cards + progress bar.
  const stats = useMemo(() => {
    const all = ROADMAP.flatMap((p) => p.items);
    return {
      total:       all.length,
      completed:   all.filter((i) => i.status === 'completed').length,
      in_progress: all.filter((i) => i.status === 'in_progress').length,
      not_started: all.filter((i) => i.status === 'not_started').length,
      rejected:    all.filter((i) => i.status === 'rejected').length,
      deferred:    all.filter((i) => i.status === 'deferred').length,
    };
  }, []);

  const completedPct   = stats.total ? Math.round((stats.completed / stats.total) * 100)   : 0;
  const inProgressPct  = stats.total ? Math.round((stats.in_progress / stats.total) * 100) : 0;

  // Apply phase + status + search filters together.
  const filteredPhases = useMemo(() => {
    const matchSearch = (item) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      const haystack = [item.title, item.title2, item.note, item.detail, item.competitor]
        .filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    };
    return ROADMAP
      .filter((p) => phaseFilter === 'all' || phaseFilter === p.title)
      .map((p) => ({
        ...p,
        items: p.items.filter((i) => {
          if (statusFilter !== 'all' && i.status !== statusFilter) return false;
          return matchSearch(i);
        }),
      }))
      .filter((p) => p.items.length > 0 || (phaseFilter !== 'all' && p.title === phaseFilter));
  }, [phaseFilter, statusFilter, search]);

  return (
    <>
      <PageHeader
        title="Feature Roadmap"
        subtitle={`${stats.completed}/${stats.total} shipped · ${stats.not_started} queued · ${stats.rejected} rejected`}
        action={
          <div style={{ display: 'flex', gap: 8 }}>
            <a href="https://github.com/SakthiInc/perkvora-client/blob/development/ROADMAP.md" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">View ROADMAP.md ↗</a>
          </div>
        }
      />

      {/* Overall progress bar (Vowsera pattern). Done segment + In-progress segment. */}
      <div className="ds-card" style={{ padding: 18, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#0F1225' }}>Overall progress</p>
          <p style={{ fontSize: 22, fontWeight: 800, color: '#6B2FD9', fontVariantNumeric: 'tabular-nums' }}>{completedPct}%</p>
        </div>
        <div style={{ height: 12, borderRadius: 999, background: '#F0F2F8', overflow: 'hidden', display: 'flex' }}>
          <div style={{ width: `${completedPct}%`, background: 'linear-gradient(90deg, #166534, #19A974)', transition: 'width 0.4s' }} />
          <div style={{ width: `${inProgressPct}%`, background: '#F59E0B', transition: 'width 0.4s' }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginTop: 14 }}>
          <Counter label="Total"        value={stats.total}        color="#6B2FD9" />
          <Counter label="Done"         value={stats.completed}    color="#166534" />
          <Counter label="In progress"  value={stats.in_progress}  color="#92400E" />
          <Counter label="Queued"       value={stats.not_started}  color="#5A6378" />
          <Counter label="Rejected"     value={stats.rejected}     color="#991B1B" muted />
        </div>
      </div>

      {/* Filters: phase pills + status pills + search */}
      <div className="ds-card" style={{ padding: 14, marginBottom: 18, display: 'grid', gap: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={pillLabel}>Phase</span>
          <FilterPill label="All phases" active={phaseFilter === 'all'} onClick={() => setPhaseFilter('all')} accent="#6B2FD9" />
          {ROADMAP.map((p) => (
            <FilterPill key={p.title}
              label={p.title.replace(/^Phase /, 'P').split(' — ')[0]}
              active={phaseFilter === p.title}
              onClick={() => setPhaseFilter(p.title)}
              accent="#6B2FD9" />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <span style={pillLabel}>Status</span>
          <FilterPill label="All" active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} accent="#6B2FD9" />
          {['completed', 'in_progress', 'not_started', 'rejected', 'deferred'].map((s) => {
            const meta = STATUS_META[s];
            return (
              <FilterPill key={s} label={meta.label}
                active={statusFilter === s}
                onClick={() => setStatusFilter(s)}
                accent={meta.fg}
                bgActive={meta.bg}
              />
            );
          })}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title, competitor, tag, or note…"
          className="form-input"
          style={{ fontSize: 13 }}
        />
      </div>

      {/* Phase sections */}
      {filteredPhases.length === 0 ? (
        <div className="ds-card" style={{ padding: 36, textAlign: 'center', color: '#8B94A8' }}>
          No items match. Try clearing filters or adjusting the search.
        </div>
      ) : filteredPhases.map((phase, phaseIdx) => (
        <PhaseSection
          key={phase.title}
          phase={phase}
          phaseIdx={phaseIdx}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      ))}

      {/* Investor / platform metrics footer */}
      <div className="ds-card" style={{ marginTop: 28, padding: 24, background: 'linear-gradient(135deg, #1A0040, #4C1D95)', color: '#fff', border: 'none' }}>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#D8C8F8', marginBottom: 4 }}>Snapshot</p>
        <h3 style={{ fontSize: 18, fontWeight: 800 }}>Platform metrics worth knowing</h3>
        <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.7)', marginTop: 4, marginBottom: 16 }}>
          Numbers as of the latest seed / config. Edit <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 6px', borderRadius: 4 }}>src/data/roadmap.js</code> when these shift.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          {INVESTOR_METRICS.map((m) => (
            <div key={m.label} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 18, marginBottom: 6 }}>{m.icon}</div>
              <p style={{ fontSize: 18, fontWeight: 800, color: '#F3EEFF', letterSpacing: '-0.01em' }}>{m.value}</p>
              <p style={{ fontSize: 11.5, color: '#D8C8F8', fontWeight: 600, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{m.label}</p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 4 }}>{m.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ============================================================ */

function PhaseSection({ phase, phaseIdx, expanded, setExpanded }) {
  const meta = STATUS_META[phase.status] || STATUS_META.not_started;

  // Per-phase completion calc — uses the FULL item list, not the filtered one,
  // so the bar reflects true progress regardless of active filters.
  const allItems = phase.items;
  const phaseTotal = allItems.length;
  const phaseDone  = allItems.filter((i) => i.status === 'completed').length;
  const phasePct   = phaseTotal ? Math.round((phaseDone / phaseTotal) * 100) : 0;
  const accent     = phase.title.startsWith('Phase 2') ? '#6B2FD9'
                   : phase.title.startsWith('Phase 1') ? '#19A974'
                   : '#94A3B8';

  return (
    <section style={{ marginBottom: 28 }}>
      <div className="ds-card" style={{ padding: 16, marginBottom: 10, borderLeft: `4px solid ${accent}` }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800, color: accent, letterSpacing: '-0.015em' }}>{phase.title}</h2>
          <span style={{ padding: '3px 10px', borderRadius: 6, background: meta.bg, color: meta.fg, fontSize: 11.5, fontWeight: 700 }}>{meta.label}</span>
          {phase.completedDate && <span style={{ fontSize: 12, color: '#8B94A8' }}>completed {phase.completedDate}</span>}
          {phase.doc && (
            <a href={`https://github.com/SakthiInc/perkvora-client/blob/development/${phase.doc}`} target="_blank" rel="noopener noreferrer"
               style={{ fontSize: 12, color: accent, fontFamily: 'ui-monospace, monospace', textDecoration: 'none', marginLeft: 'auto' }}>
              {phase.doc} ↗
            </a>
          )}
        </div>
        {phase.description && (
          <p style={{ fontSize: 12.5, color: '#5A6378', lineHeight: 1.55, marginBottom: 8 }}>{phase.description}</p>
        )}
        {phaseTotal > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: '#5A6378' }}>{phaseDone} / {phaseTotal} done</span>
              <span style={{ fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums' }}>{phasePct}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: '#F0F2F8', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${phasePct}%`, background: accent, transition: 'width 0.4s' }} />
            </div>
          </>
        )}
      </div>

      {phase.items.length === 0 ? (
        <div className="ds-card" style={{ padding: 18, fontSize: 13, color: '#8B94A8', textAlign: 'center' }}>
          No items planned yet — Phase 3 is sketched but not detailed.
        </div>
      ) : (
        <div className="ds-card" style={{ overflow: 'hidden', padding: 0 }}>
          {phase.items.map((item, itemIdx) => {
            const key = `${phaseIdx}:${itemIdx}`;
            return (
              <RoadmapRow
                key={item.n}
                item={item}
                isOpen={expanded === key}
                onToggle={() => setExpanded(expanded === key ? null : key)}
                isLast={itemIdx === phase.items.length - 1}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}

function RoadmapRow({ item, isOpen, onToggle, isLast }) {
  const meta   = STATUS_META[item.status] || STATUS_META.not_started;
  const effort = item.effort ? EFFORT_META[item.effort] : null;
  const dim    = item.status === 'rejected' || item.status === 'deferred';

  return (
    <div style={{ borderBottom: isLast ? 'none' : '1px solid #F0F2F8' }}>
      {/* Collapsed header — always visible */}
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
          padding: '14px 18px',
          display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 14, alignItems: 'center',
          opacity: dim ? 0.7 : 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 50 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#5A6378', fontVariantNumeric: 'tabular-nums' }}>#{item.n}</span>
          <span style={{ fontSize: 14 }}>{meta.icon}</span>
        </div>

        <div style={{ minWidth: 0 }}>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#0F1225', textDecoration: dim && item.status === 'rejected' ? 'line-through' : 'none' }}>{item.title}</p>
          {item.title2 && <p style={{ fontSize: 12.5, color: '#6B2FD9', marginTop: 2 }}>{item.title2}</p>}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {item.roles?.map((r) => {
            const rc = ROLE_COLORS[r] || { bg: '#F0F2F8', fg: '#5A6378' };
            return <span key={r} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 600, background: rc.bg, color: rc.fg }}>{r}</span>;
          })}
          {effort && <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, background: effort.bg, color: effort.fg, fontFamily: 'ui-monospace, monospace' }}>{effort.label}</span>}
          {item.impact > 0 && (
            <span title={`Impact ${item.impact}/10`} style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10.5, fontWeight: 700, background: '#F0F2F8', color: '#5A6378', fontFamily: 'ui-monospace, monospace' }}>
              ★ {item.impact}
            </span>
          )}
          <span style={{ padding: '2px 8px', borderRadius: 6, background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{meta.label}</span>
        </div>

        <span style={{ color: '#8B94A8', fontSize: 14, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
      </button>

      {/* Expanded panel */}
      {isOpen && (
        <div style={{ padding: '0 18px 18px', background: '#FAFBFD', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 12 }}>
          {item.note && (
            <div>
              <p style={detailLabel}>Why it matters</p>
              <p style={detailText}>{item.note}</p>
            </div>
          )}
          {item.detail && (
            <div>
              <p style={detailLabel}>Detail</p>
              <p style={detailText}>{item.detail}</p>
            </div>
          )}
          {item.competitor && (
            <div>
              <p style={detailLabel}>Where you've seen it</p>
              <p style={detailText}>{item.competitor}</p>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
            {effort && (
              <DetailStat label="Effort" value={effort.label} sub={item.effortText} bg={effort.bg} fg={effort.fg} />
            )}
            {item.impact > 0 && (
              <DetailStat label="Impact" value={`${item.impact} / 10`} sub="bigger = higher leverage" bg="#F0F2F8" fg="#5A6378" />
            )}
          </div>
          {item.tags && item.tags.length > 0 && (
            <div>
              <p style={detailLabel}>Tags</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {item.tags.map((t) => {
                  const tm = TAG_META[t] || { bg: '#F0F2F8', fg: '#5A6378' };
                  return <span key={t} style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace', background: tm.bg, color: tm.fg }}>{t}</span>;
                })}
              </div>
            </div>
          )}
          {item.files && item.files.length > 0 && (
            <div>
              <p style={detailLabel}>Files shipped</p>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {item.files.map((f) => (
                  <span key={f} style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace', background: '#0F1225', color: '#D8C8F8' }}>{f}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================ */
/* Tiny presentational helpers                                  */
/* ============================================================ */

function Counter({ label, value, color, muted }) {
  return (
    <div style={{ textAlign: 'center', opacity: muted ? 0.6 : 1 }}>
      <p style={{ fontSize: 22, fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{value}</p>
      <p style={{ fontSize: 10.5, color: '#8B94A8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, marginTop: 2 }}>{label}</p>
    </div>
  );
}

function FilterPill({ label, active, onClick, accent = '#6B2FD9', bgActive }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '5px 12px',
        borderRadius: 6,
        fontSize: 12,
        fontWeight: 600,
        border: active ? `1px solid ${accent}` : '1px solid #E4E7F0',
        background: active ? (bgActive || `${accent}15`) : '#fff',
        color: active ? accent : '#5A6378',
        cursor: 'pointer',
      }}
    >{label}</button>
  );
}

function DetailStat({ label, value, sub, bg, fg }) {
  return (
    <div style={{ background: bg, color: fg, padding: '10px 14px', borderRadius: 8 }}>
      <p style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', opacity: 0.8 }}>{label}</p>
      <p style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>{value}</p>
      {sub && <p style={{ fontSize: 11, marginTop: 2, opacity: 0.75 }}>{sub}</p>}
    </div>
  );
}

const pillLabel  = { fontSize: 11, color: '#5A6378', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginRight: 4 };
const detailLabel = { fontSize: 11, color: '#8B94A8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 };
const detailText  = { fontSize: 13, color: '#0F1225', lineHeight: 1.6 };
